import { Attribute, ChangeDetectionStrategy, Component, HostBinding, computed, input } from '@angular/core';
import { TEXT_ARIA_LEVEL, TEXT_ARIA_ROLE, TEXT_DEFAULT_TAG, type TextFlow, type TextLeading, type TextScale, type TextStrong, type TextTone, type TextToneCva, type TextType, textVariants } from './text.variants';

@Component({
  selector: 'l-text',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: '<ng-content />'
})
export class TextComponent {
  private readonly staticHostClass: string;

  public constructor(@Attribute('class') hostClassAttr: string | null) {
    const raw = hostClassAttr?.trim() ?? '';
    this.staticHostClass = raw.replace(/\s+/g, ' ').trim();
  }

  public readonly t = input<TextType>('body2');

  public readonly tone = input<TextTone | undefined>(undefined);

  public readonly strong = input<TextStrong>('inherit');

  public readonly leading = input<TextLeading>('inherit');

  public readonly scale = input<TextScale>('default');

  public readonly for = input<string | undefined>(undefined);

  public readonly flow = input<TextFlow>('contents');

  @HostBinding('class')
  protected get hostClass(): string {
    const toneKey: TextToneCva = this.tone() === undefined ? 'unset' : this.tone()!;
    const variantCls =
      textVariants({
        t: this.t(),
        tone: toneKey,
        strong: this.strong(),
        leading: this.leading(),
        scale: this.scale()
      }) ?? '';
    return [this.staticHostClass, variantCls].filter(Boolean).join(' ');
  }

  @HostBinding('style.display')
  protected get hostDisplayFlow(): TextFlow {
    return this.flow();
  }

  @HostBinding('attr.role')
  protected get hostRole(): string | null {
    return TEXT_ARIA_ROLE[this.t()] ?? null;
  }

  @HostBinding('attr.aria-level')
  protected get hostAriaLevel(): number | null {
    return TEXT_ARIA_LEVEL[this.t()] ?? null;
  }

  @HostBinding('attr.for')
  protected get hostFor(): string | null {
    return this.for() ?? null;
  }

  public readonly defaultTag = computed(() => TEXT_DEFAULT_TAG[this.t()]);
}
